import React, { useCallback, useState, Suspense } from "react";
import { logTechCheck } from "utils/sentryLogger";
import { ActionButton } from "./ActionButton";
import { HelpIcon } from "./icons/HelpIcon";

// Lazy-load — the overlay is only fetched on click.
const TechCheckFlow = React.lazy(() =>
  import("components/pages/Session/TechCheck").then((m) => ({ default: m.TechCheckFlow }))
);

const ENTRY_POINT = "session_action_bar_help";

/**
 * In-session Help CTA. Mounts the same TechCheck overlay that fires from
 * SessionTrigger's "I need help" button pre-session, so users can re-run
 * the service / speaker / mic diagnostics without leaving the session.
 *
 * The entryPoint prop differentiates the two trigger points in Sentry via
 * the `tech_check_opened` event.
 */
export const HelpButton = () => {
  const [showTechCheck, setShowTechCheck] = useState(false);

  // Log the click BEFORE the overlay mounts so a crash during mount is still
  // attributable to the user action. The flow-level `tech_check_opened` still
  // fires on successful mount via useTechCheckLogger.
  const handleClick = useCallback(() => {
    logTechCheck("tech_check_help_button_clicked", {
      event: "tech_check_help_button_clicked",
      entryPoint: ENTRY_POINT,
    });
    setShowTechCheck(true);
  }, []);
  const handleClose = useCallback(() => setShowTechCheck(false), []);

  return (
    <>
      <ActionButton tooltip="Help" onClick={handleClick}>
        <HelpIcon />
      </ActionButton>
      {showTechCheck && (
        <Suspense fallback={null}>
          <TechCheckFlow onClose={handleClose} entryPoint={ENTRY_POINT} />
        </Suspense>
      )}
    </>
  );
};
